import { supabase } from '../../lib/supabase'
import { getCurrentUserId } from '../../lib/auth'
import { createAuditLog } from '../../lib/audit'
import type {
  RecurringExpenseTemplate,
  RecurringExpenseTemplateInput,
  PendingRecurringExpense,
  DueRecurringExpense,
} from '../../types/financial'

// ============================================================
// RECURRING EXPENSE TEMPLATES
// ============================================================

export async function getRecurringExpenseTemplates(
  propertyId: string
): Promise<RecurringExpenseTemplate[]> {
  const { data, error } = await supabase
    .rpc('get_recurring_expense_templates', { p_property_id: propertyId })

  if (error) throw error
  return (data || []) as RecurringExpenseTemplate[]
}

export async function getAllRecurringExpenseTemplates(): Promise<RecurringExpenseTemplate[]> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .rpc('get_all_recurring_expense_templates', { p_landlord_id: userId })

  if (error) throw error
  return (data || []) as RecurringExpenseTemplate[]
}

export async function createRecurringExpenseTemplate(
  input: RecurringExpenseTemplateInput
): Promise<RecurringExpenseTemplate> {
  const { data, error } = await supabase
    .rpc('create_recurring_expense_template', {
      p_property_id: input.property_id,
      p_name: input.name,
      p_category: input.category,
      p_amount_pence: input.amount_pence,
      p_frequency: input.frequency,
      p_start_date: input.start_date,
      p_end_date: input.end_date || null,
      p_notes: input.notes || null,
      p_supplier_name: input.supplier_name || null,
    })

  if (error) throw error
  return data as RecurringExpenseTemplate
}

export async function updateRecurringExpenseTemplate(
  id: string,
  input: Partial<RecurringExpenseTemplateInput>
): Promise<RecurringExpenseTemplate> {
  const { data, error } = await supabase
    .rpc('update_recurring_expense_template', {
      p_template_id: id,
      p_name: input.name || null,
      p_category: input.category || null,
      p_amount_pence: input.amount_pence || null,
      p_frequency: input.frequency || null,
      p_end_date: input.end_date ?? null,
      p_notes: input.notes ?? null,
      p_supplier_name: input.supplier_name ?? null,
    })

  if (error) throw error
  return data as RecurringExpenseTemplate
}

export async function deactivateRecurringExpenseTemplate(
  id: string
): Promise<RecurringExpenseTemplate> {
  const { data, error } = await supabase
    .rpc('deactivate_recurring_expense_template', { p_template_id: id })

  if (error) throw error
  return data as RecurringExpenseTemplate
}

export async function reactivateRecurringExpenseTemplate(
  id: string
): Promise<RecurringExpenseTemplate> {
  const { data, error } = await supabase
    .rpc('reactivate_recurring_expense_template', { p_template_id: id })

  if (error) throw error
  return data as RecurringExpenseTemplate
}

// ============================================================
// PENDING RECURRING EXPENSES
// ============================================================

export async function getPendingRecurringExpenses(): Promise<PendingRecurringExpense[]> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .rpc('get_pending_recurring_expenses', { p_landlord_id: userId })

  if (error) throw error
  return (data || []) as PendingRecurringExpense[]
}

export async function confirmRecurringExpense(
  expenseId: string,
  amountPence?: number
): Promise<void> {
  const { error } = await supabase
    .rpc('confirm_recurring_expense', {
      p_expense_id: expenseId,
      p_amount_pence: amountPence || null,
    })

  if (error) throw error

}

export async function skipRecurringExpense(
  expenseId: string,
  reason?: string
): Promise<void> {
  const { error } = await supabase
    .rpc('skip_recurring_expense', {
      p_expense_id: expenseId,
      p_skip_reason: reason || null,
    })

  if (error) throw error
}

// ============================================================
// DUE RECURRING EXPENSES (on-demand, no edge function)
// ============================================================

export async function getDueRecurringExpenses(): Promise<DueRecurringExpense[]> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .rpc('get_due_recurring_expenses', { p_landlord_id: userId })

  if (error) throw error
  return (data || []) as DueRecurringExpense[]
}

export async function confirmDueRecurringExpense(
  templateId: string,
  amountPence?: number
): Promise<void> {
  const { error } = await supabase
    .rpc('confirm_due_recurring_expense', {
      p_template_id: templateId,
      p_amount_pence: amountPence || null,
    })

  if (error) throw error
}

export async function skipDueRecurringExpense(
  templateId: string
): Promise<void> {
  const { error } = await supabase
    .rpc('skip_due_recurring_expense', { p_template_id: templateId })

  if (error) throw error
}
